import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Glasses, Network, UserCheck, UserPen, Users } from "lucide-react";
import type { FacilityPermissionLevel } from "@arc/contracts";
import { FACILITY_PERMISSION_LEVELS } from "@arc/contracts";
import { cn } from "@/lib/utils";

/**
 * The ONE description of the five permission levels (ADR-004's ladder as the legacy names them — View / Edit / Review /
 * Manage facilities / Admin, owner 2026-09-11) for every surface: the facility drawer's "Your access", the roles
 * editor's ladder, the grants tree. `rank` is the ladder position (1..5); the icons are the legacy's (glasses, user-pen,
 * user-check, sitemap, users).
 */
export const PERMISSION_LEVEL_META = {
  view: { rank: 1, labelKey: "permissions.level1", shortKey: "permissionLevels.viewShort", descriptionKeys: ["permissionLevels.viewDesc1"], Icon: Glasses },
  manuals_manage: {
    rank: 2,
    labelKey: "permissions.level2",
    shortKey: "permissionLevels.editShort",
    descriptionKeys: ["permissionLevels.editDesc1", "permissionLevels.editDesc2"],
    Icon: UserPen,
  },
  confirm: {
    rank: 3,
    // The owner named this level "Review" (2026-09-11), where the legacy German says "Kontrollieren" — so the label
    // is the rewrite's own key, not the legacy `permissions.level3`, and carries that German.
    labelKey: "permissionLevels.review",
    shortKey: "permissionLevels.reviewShort",
    descriptionKeys: ["permissionLevels.reviewDesc1", "permissionLevels.reviewDesc2", "permissionLevels.reviewDesc3"],
    Icon: UserCheck,
  },
  manage: {
    rank: 4,
    labelKey: "permissions.level4",
    shortKey: "permissionLevels.manageShort",
    descriptionKeys: ["permissionLevels.manageDesc1", "permissionLevels.manageDesc2", "permissionLevels.manageDesc3"],
    Icon: Network,
  },
  access_manage: {
    rank: 5,
    // Owner's "Admin" over the legacy's "Administrate"; the German stays the legacy's "Administrator".
    labelKey: "permissionLevels.admin",
    shortKey: "permissionLevels.adminShort",
    descriptionKeys: [
      "permissionLevels.adminDesc1",
      "permissionLevels.adminDesc2",
      "permissionLevels.adminDesc3",
      "permissionLevels.adminDesc4",
    ],
    Icon: Users,
  },
} as const satisfies Record<
  FacilityPermissionLevel,
  { rank: number; labelKey: string; shortKey: string; descriptionKeys: readonly string[]; Icon: LucideIcon }
>;

/** The ladder top-down (5 → 1), as the legacy renders it. */
export const PERMISSION_LEVELS_DESC: readonly FacilityPermissionLevel[] = [...FACILITY_PERMISSION_LEVELS].reverse();

/** `hpfp` → its rank (0 for "no level set"). */
export function permissionRank(level: FacilityPermissionLevel | null): number {
  return level === null ? 0 : PERMISSION_LEVEL_META[level].rank;
}

/** The unset state's glyph — the legacy's warning icon. */
export const NO_LEVEL_ICON = AlertTriangle;

/** A glyph + label pill for a level (the `ManualStateBadge` treatment: colour is never the only signal). */
export function PermissionLevelBadge({ level, className, prefix }: { level: FacilityPermissionLevel; className?: string; prefix?: string }) {
  const { t } = useTranslation();
  const { labelKey, Icon } = PERMISSION_LEVEL_META[level];
  const label = t(labelKey);
  return (
    <span className={cn("bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium", className)}>
      <Icon aria-hidden="true" className="size-3.5" />
      {prefix ? `${prefix} ${label}` : label}
    </span>
  );
}
